import React, { useRef, useState } from 'react';
import { Tag, Badge, Space, Button, message, Modal, Row, Col, Popconfirm } from 'antd';
import { EyeOutlined, ShopOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { MainBody, MainBodyLabels } from '../../types/entity/main-body';
import { mainBodyApi } from '../../services/manager/main-body';
import { UpsertModal, type FormFieldConfig } from '../../components/Templates/UpsertModal';
import List, { ListRef } from '../../components/Templates/List';
import type { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';

const MainBodies: React.FC = () => {
  const listRef = useRef<ListRef>(null);
  const [modalState, setModalState] = useState<{ open: boolean; mode: 'create' | 'edit'; record: MainBody | null }>({
    open: false,
    mode: 'create',
    record: null
  });

  const queryFields: FieldConfig[] = [
    { key: 'name', label: MainBodyLabels.name, type: 'string' },
    { key: 'status', label: MainBodyLabels.status, type: 'select', options: [{ label: '启用', value: 1 }, { label: '禁用', value: 0 }] },
  ];

  const formFields: FormFieldConfig[] = [
    { name: 'name', label: MainBodyLabels.name, type: 'text', required: true },
    { name: 'code', label: MainBodyLabels.code, type: 'text', rules: [{ max: 50, message: '主体代码最多50个字符' }] },
    { name: 'description', label: MainBodyLabels.description, type: 'textarea', rules: [{ max: 500, message: '描述最多500个字符' }] },
    { name: 'status', label: MainBodyLabels.status, type: 'select', initialValue: 1, options: [{ label: '启用', value: 1 }, { label: '禁用', value: 0 }] },
  ];

  const columns: ColumnsType<MainBody> = [
    { title: 'ID', dataIndex: 'id', width: 80, fixed: 'left' },
    { title: MainBodyLabels.name, dataIndex: 'name', width: 150, fixed: 'left', render: (t) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{t}</span> },
    { title: MainBodyLabels.code, dataIndex: 'code', width: 120, render: (t) => <code>{t || '-'}</code> },
    { title: MainBodyLabels.description, dataIndex: 'description', width: 200, ellipsis: true },
    { title: MainBodyLabels.status, dataIndex: 'status', width: 100, render: (s) => <Tag color={s === 1 ? 'green' : 'red'}>{s === 1 ? '启用' : '禁用'}</Tag> },
    { title: '关联店铺数', dataIndex: 'shop_count', width: 120, render: (c) => <Badge count={c} showZero style={{ backgroundColor: c > 0 ? '#52c41a' : '#d9d9d9' }} /> },
    { title: '创建时间', dataIndex: 'created_at', width: 180, render: (t) => new Date(t).toLocaleString('zh-CN') },
    { title: '更新时间', dataIndex: 'updated_at', width: 180, render: (t) => new Date(t).toLocaleString('zh-CN') },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} size="small" onClick={() => handleView(record)}>查看</Button>
          <Button type="link" icon={<EditOutlined />} size="small" onClick={() => setModalState({ open: true, mode: 'edit', record })}>编辑</Button>
          <Button type="link" icon={<ShopOutlined />} size="small" onClick={() => handleViewShops(record)}>店铺</Button>
          <Button 
            type="link" 
            icon={<DeleteOutlined />} 
            size="small" 
            danger 
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  const handleDelete = (record: MainBody) => {
    if ((record.shop_count || 0) > 0) {
      message.warning('该主体下还有关联店铺，无法删除');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除主体 "${record.name}" 吗？`,
      onOk: async () => {
        try {
          await mainBodyApi.deleteById(String(record.id));
          message.success('删除成功');
          listRef.current?.reload();
        } catch (error: any) {
          message.error(error.message || '删除失败');
        }
      }
    });
  };

  const handleView = (record: MainBody) => {
    Modal.info({
      title: '主体详情',
      width: 600,
      content: (
        <div style={{ marginTop: 16 }}>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>ID:</strong></Col>
            <Col span={16}>{record.id}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{MainBodyLabels.name}:</strong></Col>
            <Col span={16}>{record.name}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{MainBodyLabels.code}:</strong></Col>
            <Col span={16}>{record.code || '-'}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{MainBodyLabels.description}:</strong></Col>
            <Col span={16}>{record.description || '-'}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{MainBodyLabels.status}:</strong></Col>
            <Col span={16}>
              <Tag color={record.status === 1 ? 'green' : 'red'}>
                {record.status === 1 ? '启用' : '禁用'}
              </Tag>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>关联店铺数:</strong></Col>
            <Col span={16}>
              <Badge count={record.shop_count || 0} showZero style={{ backgroundColor: (record.shop_count || 0) > 0 ? '#52c41a' : '#d9d9d9' }} />
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>创建时间:</strong></Col>
            <Col span={16}>{new Date(record.created_at || '').toLocaleString('zh-CN')}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>更新时间:</strong></Col>
            <Col span={16}>{new Date(record.updated_at || '').toLocaleString('zh-CN')}</Col>
          </Row>
        </div>
      )
    });
  };

  const handleViewShops = (record: MainBody) => {
    message.info(`查看主体 "${record.name}" 的店铺列表功能即将开放`);
  };

  return (
    <>
      <List<MainBody>
        ref={listRef}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>主体管理</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ open: true, mode: 'create', record: null })}>
              新增主体
            </Button>
          </div>
        }
        queryFields={queryFields}
        fetcher={async (params) => {
          const res = await mainBodyApi.list(params);
          return { rows: res.data.items, pagination: res.data.pagination };
        }}
        buildParams={(q) => {
           const params: any = { page: q.page, pageSize: q.pageSize };
           if (q.filter) {
               Object.entries(q.filter).forEach(([key, val]) => {
                  if (val && typeof val === 'object' && 'value' in val) params[key] = val.value;
               });
           }
           return params;
        }}
        columns={columns}
        rowKey={(r) => String(r.id)}
        tableProps={{ scroll: { x: 'max-content' } }}
      />

      <UpsertModal<MainBody>
        title={modalState.mode === 'create' ? '新增主体' : '编辑主体'}
        open={modalState.open}
        mode={modalState.mode}
        record={modalState.record}
        onCancel={() => setModalState({ ...modalState, open: false })}
        onSuccess={() => {
          setModalState({ ...modalState, open: false });
          listRef.current?.reload();
        }}
        createApi={(data) => mainBodyApi.create(data)}
        updateApi={(id, data) => mainBodyApi.updateById(id, data)}
        formFields={formFields}
      />
    </>
  );
};

export default MainBodies;
